import { useCallback, useEffect, useId, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Pill } from '../../ui/DataBits';
import { Dialog } from '../../ui/Dialog';
import { AdminFrame } from './AdminFrame';
import {
  ClassesData,
  errorText,
  fmtDate,
  fmtDateTime,
  ops,
  PeopleData,
  Person,
  RoleSpec,
  rpc,
  SchoolDetail,
  SchoolRole,
} from './api';
import { CopyButton, EnterSupportDialog, RolePicker } from './bits';
import { roleReach } from './reach';
import { useClasses } from './useClasses';
import { roleLabel } from '../../ui/roles';
import { PersonStatusPill, RolePill } from './pills';
import { SchoolCtx, SupportOnly } from './SchoolPage';
import { AuditList } from './AuditList';

// What admin-ops answers for a new test account. pending_roles: roles that
// wait for the primary head's approval (0011).
type Made = { username: string; expires_at: string; pending_roles?: SchoolRole[] };

const expired = (p: Person) => p.is_test && !!p.test_expires_at && new Date(p.test_expires_at).getTime() <= Date.now();

// Staff tab: everyone with a role here (TEST badge on test accounts), their
// roles, classes and sign-in; invites waiting. Sandbox schools get test
// accounts (C5); Customer schools get invites (C1), queued until an email
// provider exists.
export function StaffTab(ctx: SchoolCtx) {
  const { t, locale } = useI18n();
  const s = ctx.detail.school;
  const sandbox = s.school_type === 'sandbox';
  const [data, setData] = useState<PeopleData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [classes, loadClasses] = useClasses(s.id);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const announce = useAnnounce();

  const load = useCallback(async () => {
    const r = await rpc<PeopleData>('platform_school_people', { p_school_id: s.id });
    if (r.error) setError(errorText(t, r.error));
    else setData(r.data);
  }, [s.id, t]);

  useEffect(() => {
    load();
  }, [load]);

  async function cancelInvite() {
    if (!cancelId) return;
    setBusy(true);
    const r = await rpc('platform_cancel_invite', { p_invite_id: cancelId });
    setBusy(false);
    if (r.error) return setError(errorText(t, r.error));
    setCancelId(null);
    announce(t['pa.inviteCancelled']);
    await load();
    await ctx.reload();
  }

  return (
    <>
      <section className="panel p5" aria-labelledby="people-title">
        <div className="panel-head wrap">
          <h2 className="panel-title" id="people-title">
            {t['pa.people']}
          </h2>
          {ctx.inSupport && s.state !== 'offboarded' && (
            <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
              {sandbox ? t['pa.addTestAccount'] : t['pa.inviteStaff']}
            </button>
          )}
        </div>
        {error ? (
          <p className="error-text" role="alert">
            {error}
          </p>
        ) : !data ? (
          <p role="status" className="text-muted">
            {t.loading}
          </p>
        ) : data.people.length === 0 ? (
          <p className="text-muted">{t['pa.noPeople']}</p>
        ) : (
          <ul className="people-list">
            {data.people.map((p) => (
              <li key={p.id} className="person-row">
                <div className="person-main">
                  <Link to={`/admin/schools/${s.id}/people/${p.id}`} className="person-name">
                    {p.display_name}
                  </Link>{' '}
                  {p.is_test && <span className="test-badge">{t['ctx.testBadge']}</span>}
                  <span className="person-email">{p.email}</span>
                </div>
                <span className="pill-row">
                  {(p.roles ?? []).map((g) => (
                    <RolePill key={g.grant_id} grant={g} />
                  ))}
                  <PersonStatusPill status={p.status} isTest={p.is_test} expired={expired(p)} />
                </span>
                <span className="person-meta">
                  {p.assignments.length > 0
                    ? p.assignments.map((a) => a.class_label ?? t['pa.gradeLeadOf'](a.grade_label ?? '')).join(', ')
                    : (p.roles ?? []).some((g) => g.role === 'teacher')
                      ? <Pill kind="warn">{t['pa.noClasses']}</Pill>
                      : null}
                </span>
                <span className="person-meta text-muted">
                  {p.is_test && p.test_expires_at ? t['pa.expires'](fmtDate(locale, p.test_expires_at)) + ' · ' : ''}
                  {t['pa.lastSignIn'](p.last_sign_in_at ? fmtDateTime(locale, p.last_sign_in_at) : t['pa.never'])}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {data && data.invites.length > 0 && (
        <section className="panel p5" aria-labelledby="inv-title">
          <h2 className="panel-title" id="inv-title">
            {t['pa.invites']}
          </h2>
          <p className="form-hint">{t['pa.invitesQueuedNote']}</p>
          <ul className="school-list">
            {data.invites.map((i) => (
              <li key={i.id}>
                <span>
                  {i.display_name} · {i.email} · {i.roles.map((r) => t[`pa.roleName_${r.role}`]).join(', ')}
                </span>
                <span className="pill-row">
                  <Pill kind="warn">{i.status === 'queued' ? t['pa.inviteQueued'] : t['pa.inviteSent']}</Pill>
                  {ctx.inSupport && (
                    <button type="button" className="link-btn" onClick={() => setCancelId(i.id)} aria-label={t['pa.cancelInviteFor'](i.display_name)}>
                      {t['pa.cancelInvite']}
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <AddPersonDialog
        open={adding}
        detail={ctx.detail}
        classes={classes}
        onClose={() => setAdding(false)}
        onDone={async () => {
          await load();
          await loadClasses();
          await ctx.reload();
        }}
      />
      <Dialog
        open={!!cancelId}
        title={t['pa.cancelInvite']}
        intro={<p className="form-hint">{t['pa.cancelInviteIntro']}</p>}
        submitLabel={t['pa.cancelInvite']}
        danger
        busy={busy}
        onSubmit={cancelInvite}
        onClose={() => setCancelId(null)}
        closeLabel={t['pa.keep']}
      />
    </>
  );
}

function AddPersonDialog({
  open,
  detail,
  classes,
  onClose,
  onDone,
}: {
  open: boolean;
  detail: SchoolDetail;
  classes: ClassesData | null;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { t, locale } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const s = detail.school;
  const sandbox = s.school_type === 'sandbox';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<RoleSpec[]>([{ role: 'teacher' }]);
  const [days, setDays] = useState(90);
  const [lang, setLang] = useState(s.default_language);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<null | (Made & { password: string }) | 'invited'>(null);

  function reset() {
    setName('');
    setEmail('');
    setRoles([{ role: 'teacher' }]);
    setDays(90);
    setError(null);
    setMade(null);
  }

  async function submit() {
    if (made) {
      reset();
      return;
    }
    setBusy(true);
    setError(null);
    if (sandbox) {
      const r = await ops<Made & { password: string }>('create_test_account', {
        school_id: s.id,
        display_name: name,
        roles,
        expires_days: days,
      });
      setBusy(false);
      if (r.error) return setError(errorText(t, r.error));
      setMade(r.data);
      announce(t['pa.testCreated'](name));
    } else {
      const r = await rpc('platform_queue_invite', {
        p_school_id: s.id,
        p_email: email,
        p_display_name: name,
        p_roles: roles,
        p_language: lang,
      });
      setBusy(false);
      if (r.error) return setError(errorText(t, r.error));
      setMade('invited');
      announce(t['pa.inviteQueuedDone'](name));
    }
    await onDone();
  }

  return (
    <Dialog
      open={open}
      wide
      title={sandbox ? t['pa.addTestAccount'] : t['pa.inviteStaff']}
      intro={<p className="form-hint">{sandbox ? t['pa.testIntro'] : t['pa.inviteIntro']}</p>}
      submitLabel={made ? t['pa.addAnother'] : sandbox ? t['pa.createTest'] : t['pa.queueInvite']}
      busy={busy}
      error={error}
      submitDisabled={!made && (!name.trim() || roles.length === 0)}
      onSubmit={submit}
      onClose={() => {
        reset();
        onClose();
      }}
      closeLabel={made ? t['pa.close'] : undefined}
    >
      {made === 'invited' ? (
        <p className="notice">{t['pa.invitesQueuedNote']}</p>
      ) : made ? (
        <div className="reveal" role="region" aria-label={t['pa.credsTitle']}>
          <p>
            <strong>{t['pa.credsTitle']}</strong> · {t['pa.expires'](fmtDate(locale, made.expires_at))}
          </p>
          <p className="form-hint">{t['pa.credsOnce']}</p>
          <p>
            {t['pa.username']}: <code>{made.username}</code> <CopyButton text={made.username} label={t['pa.copyUsername']} />
          </p>
          <p>
            {t['pa.password']}: <code className="secret">{made.password}</code>{' '}
            <CopyButton text={made.password} label={t['pa.copyPassword']} />
          </p>
          <p className="form-hint">{t['pa.schoolCodeForLogin'](s.code)}</p>
          {made.pending_roles && made.pending_roles.length > 0 && (
            <p className="notice">{t['pa.pendingRolesNote'](made.pending_roles.map((r) => roleLabel(t, r)).join(', '))}</p>
          )}
        </div>
      ) : (
        <>
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-n`}>
              {sandbox ? t['pa.fictionalName'] : t['pa.fullName']}
            </label>
            <input
              id={`${id}-n`}
              className="field"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
              placeholder={sandbox ? t['pa.fictionalExample'] : undefined}
            />
          </div>
          {!sandbox && (
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-e`}>
                {t['pa.workEmail']}
              </label>
              <input id={`${id}-e`} className="field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
            </div>
          )}
          <RolePicker value={roles} onChange={setRoles} classes={classes} />
          {sandbox ? (
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-x`}>
                {t['pa.expiresIn']}
              </label>
              <span className="select-wrap">
                <select id={`${id}-x`} className="select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
                  {[90, 60, 30, 14, 7].map((d) => (
                    <option key={d} value={d}>
                      {t['pa.nDays'](d)}
                    </option>
                  ))}
                </select>
              </span>
            </div>
          ) : (
            <fieldset className="inline-radios">
              <legend className="field-label">{t['pa.inviteLanguage']}</legend>
              {(['fi', 'sv', 'en'] as const).map((l) => (
                <label key={l}>
                  <input type="radio" name={`${id}-l`} checked={lang === l} onChange={() => setLang(l)} /> {t[`pa.lang_${l}`]}
                </label>
              ))}
            </fieldset>
          )}
          {roles.some((r) => r.role !== 'teacher') && !sandbox && <p className="notice">{t['pa.wideningNote']}</p>}
        </>
      )}
    </Dialog>
  );
}

// ── Person page ────────────────────────────────────────────────────────────

export function PersonPage() {
  const { t } = useI18n();
  const [title, setTitle] = useState('');
  return (
    <AdminFrame section="schools" title={title || t['pa.person']}>
      {({ support, refreshSupport }) => (
        <PersonBody onTitle={setTitle} supportSchool={support.active ? support.school_id : null} refreshSupport={refreshSupport} />
      )}
    </AdminFrame>
  );
}

type Action =
  | 'edit'
  | 'role'
  | 'assign'
  | 'signout'
  | 'mfa'
  | 'password'
  | 'extend'
  | 'deactivate'
  | 'reactivate'
  | 'erase'
  | 'primary'
  | 'appoint'
  | { revoke: string; label: string }
  | { unassign: string; label: string };

function PersonBody({
  onTitle,
  supportSchool,
  refreshSupport,
}: {
  onTitle: (s: string) => void;
  supportSchool: string | null;
  refreshSupport: () => Promise<void>;
}) {
  const { t, locale } = useI18n();
  const announce = useAnnounce();
  const navigate = useNavigate();
  const id = useId();
  const { id: schoolId = '', uid = '' } = useParams();
  const [detail, setDetail] = useState<SchoolDetail | null>(null);
  const [person, setPerson] = useState<Person | null | undefined>(undefined);
  const [gated, setGated] = useState(false);
  const [classes, loadClasses] = useClasses(schoolId, !gated);
  const [error, setError] = useState<string | null>(null);
  const [act, setAct] = useState<Action | null>(null);
  const [busy, setBusy] = useState(false);
  const [actError, setActError] = useState<string | null>(null);
  const [supportOpen, setSupportOpen] = useState(false);
  // dialog fields
  const [name, setName] = useState('');
  const [lang, setLang] = useState<string>('');
  const [roleSpec, setRoleSpec] = useState<RoleSpec[]>([]);
  const [kind, setKind] = useState<'homeroom' | 'subject' | 'grade_lead'>('subject');
  const [picked, setPicked] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState('');
  const [days, setDays] = useState(90);
  const [restore, setRestore] = useState(true);
  const [typed, setTyped] = useState('');
  const [instrFrom, setInstrFrom] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    // A person's page is inside the school: support mode first (0010).
    const d = await rpc<SchoolDetail>('platform_school_detail', { p_school_id: schoolId });
    if (d.error) {
      setError(errorText(t, d.error));
      return;
    }
    setDetail(d.data);
    setGated(!d.data.in_support);
    if (!d.data.in_support) {
      setPerson(null);
      onTitle(d.data.school.name);
      return;
    }
    const p = await rpc<PeopleData>('platform_school_people', { p_school_id: schoolId });
    if (p.error) {
      setError(errorText(t, p.error));
      return;
    }
    const me = p.data.people.find((x) => x.id === uid) ?? null;
    setPerson(me);
    if (me) onTitle(me.display_name);
  }, [schoolId, uid, t, onTitle]);

  useEffect(() => {
    load();
  }, [load, supportSchool]);

  if (error) {
    return (
      <p className="error-text" role="alert">
        {error}
      </p>
    );
  }
  if (!detail || person === undefined) return <p role="status" className="text-muted">{t.loading}</p>;
  const s = detail.school;
  const back = `/admin/schools/${s.id}/staff`;
  if (gated) {
    return (
      <>
        <p className="crumbs">
          <Link to="/admin/schools">{t['admin.navSchools']}</Link> › <Link to={`/admin/schools/${s.id}`}>{s.name}</Link> ›{' '}
          <span aria-current="page">{t['pa.tabStaffPlain']}</span>
        </p>
        <SupportOnly
          ctx={{ detail, reload: load, inSupport: false, askSupport: () => setSupportOpen(true) }}
          what={t['pa.soPerson']}
        />
        <EnterSupportDialog
          open={supportOpen}
          schoolId={s.id}
          schoolName={s.name}
          defaultReason="account_recovery"
          onClose={() => setSupportOpen(false)}
          onEntered={async () => {
            setSupportOpen(false);
            await refreshSupport();
            await load();
          }}
        />
      </>
    );
  }
  if (!person) {
    return (
      <section className="panel p5">
        <p>{t['pa.personGone']}</p>
        <p>
          <Link to={back}>{t['pa.backToStaff']}</Link>
        </p>
      </section>
    );
  }

  const p = person;
  const roles = p.roles ?? [];
  const isTeacher = roles.some((r) => r.role === 'teacher' && r.status === 'active');
  const isAdminRole = roles.some((r) => r.role === 'school_admin' && r.status === 'active');
  const isPrimary = roles.some((r) => r.role === 'head_of_school' && r.is_primary && r.status === 'active');
  const exp = expired(p);
  const sup = detail.in_support;
  const ask = () => setSupportOpen(true);

  function open(a: Action) {
    setActError(null);
    setNote('');
    setTyped('');
    setInstrFrom('');
    setReason('');
    setDate('');
    setSecret(null);
    setResult(null);
    setPicked([]);
    if (a === 'edit') {
      setName(p.display_name);
      setLang(p.preferred_language ?? '');
    }
    if (a === 'role') setRoleSpec([]);
    if (a === 'assign') setKind(p.assignments.some((x) => x.kind === 'homeroom') ? 'subject' : 'homeroom');
    if (a === 'deactivate') setReason('left_school');
    if (a === 'erase') setReason(p.is_test ? 'test_cleanup' : 'left_school');
    if (!sup) return ask();
    setAct(a);
  }

  async function run<T>(fn: () => Promise<{ error: { code: string; message: string } | null; data: T | null }>, after?: (d: T) => void) {
    setBusy(true);
    setActError(null);
    const r = await fn();
    setBusy(false);
    if (r.error) return setActError(errorText(t, r.error));
    if (after) after(r.data as T);
    else {
      setAct(null);
      announce(t['pa.saved']);
    }
    await load();
    await loadClasses();
  }

  const close = () => setAct(null);
  const held = roles.map((r) => r.role);

  return (
    <>
      <p className="crumbs">
        <Link to="/admin/schools">{t['admin.navSchools']}</Link> › <Link to={`/admin/schools/${s.id}`}>{s.name}</Link> ›{' '}
        <Link to={back}>{t['pa.tabStaffPlain']}</Link> › <span aria-current="page">{p.display_name}</span>
      </p>


      <div className="two-col">
        <section className="panel p5" aria-labelledby={`${id}-d`}>
          <div className="panel-head">
            <h2 className="panel-title" id={`${id}-d`}>
              {t['pa.details']}
            </h2>
            {sup && (
              <button type="button" className="link-btn" onClick={() => open('edit')}>
                {t['pa.edit']}
              </button>
            )}
          </div>
          <dl className="kv">
            <div>
              <dt>{t['pa.name']}</dt>
              <dd>
                {p.display_name} {p.is_test && <span className="test-badge">{t['ctx.testBadge']}</span>}
              </dd>
            </div>
            <div>
              <dt>{p.is_test ? t['pa.username'] : t['admin.colEmail']}</dt>
              <dd className="break">{p.email ?? '–'}</dd>
            </div>
            <div>
              <dt>{t['pa.status']}</dt>
              <dd>
                <PersonStatusPill status={p.status} isTest={p.is_test} expired={exp} />
                {p.reactivate_on && ` · ${t['pa.reactivatesOn'](fmtDate(locale, p.reactivate_on))}`}
              </dd>
            </div>
            {p.is_test && (
              <div>
                <dt>{t['pa.expiry']}</dt>
                <dd>{fmtDate(locale, p.test_expires_at)}</dd>
              </div>
            )}
            <div>
              <dt>{t['pa.language']}</dt>
              <dd>{p.preferred_language ? t[`pa.lang_${p.preferred_language}`] : t['pa.schoolDefault']}</dd>
            </div>
            <div>
              <dt>{t['pa.lastSignInLabel']}</dt>
              <dd>{p.last_sign_in_at ? fmtDateTime(locale, p.last_sign_in_at) : t['pa.never']}</dd>
            </div>
            {isAdminRole && (
              <div>
                <dt>{t['pa.authenticators']}</dt>
                <dd>{p.mfa_factors}</dd>
              </div>
            )}
            {p.other_schools > 0 && (
              <div>
                <dt>{t['pa.otherSchools']}</dt>
                <dd>{t['pa.otherSchoolsN'](p.other_schools)}</dd>
              </div>
            )}
          </dl>
        </section>

        <section className="panel p5" aria-labelledby={`${id}-a`}>
          <h2 className="panel-title" id={`${id}-a`}>
            {t['pa.account']}
          </h2>
          <ul className="action-list">
            <li>
              <button type="button" className="link-btn" onClick={() => open('signout')} disabled={s.state === 'offboarded'}>
                {t['pa.signOutEverywhere']}
              </button>
            </li>
            {p.is_test && (
              <>
                <li>
                  <button type="button" className="link-btn" onClick={() => open('password')}>
                    {t['pa.resetPassword']}
                  </button>
                </li>
                <li>
                  <button type="button" className="link-btn" onClick={() => open('extend')}>
                    {t['pa.extendExpiry']}
                  </button>
                </li>
              </>
            )}
            {isAdminRole && !p.is_test && (
              <li>
                <button type="button" className="link-btn" onClick={() => open('mfa')}>
                  {t['pa.resetMfa']}
                </button>
              </li>
            )}
            {p.status === 'active' ? (
              <li>
                <button type="button" className="link-btn danger" onClick={() => open('deactivate')}>
                  {t['pa.deactivate']}
                </button>
              </li>
            ) : (
              <li>
                <button type="button" className="link-btn" onClick={() => open('reactivate')}>
                  {t['pa.reactivate']}
                </button>
              </li>
            )}
            {(p.is_test || p.status === 'deactivated') && (
              <li>
                <button type="button" className="link-btn danger" onClick={() => open('erase')}>
                  {t['pa.erase']}
                </button>
              </li>
            )}
          </ul>
          {!p.is_test && p.status === 'active' && <p className="form-hint">{t['pa.eraseAfterDeactivate']}</p>}
        </section>
      </div>

      <section className="panel p5" aria-labelledby={`${id}-r`}>
        <div className="panel-head">
          <h2 className="panel-title" id={`${id}-r`}>
            {t['pa.roles']}
          </h2>
          {sup && p.status === 'active' && held.length < 4 && (
            <button type="button" className="btn btn-ghost" onClick={() => open('role')}>
              {t['pa.addRole']}
            </button>
          )}
        </div>
        <ul className="school-list">
          {roles.map((g) => (
            <li key={g.grant_id}>
              <span className="pill-row">
                <RolePill grant={g} />
              </span>
              <span className="pill-row">
                {g.role === 'head_of_school' && !g.is_primary && g.status === 'active' && sup && (
                  <button type="button" className="link-btn" onClick={() => open('primary')}>
                    {t['pa.makePrimaryAction']}
                  </button>
                )}
                {sup && (
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => open({ revoke: g.grant_id, label: t[`pa.roleName_${g.role}`] })}
                    aria-label={t['pa.removeRoleFor'](t[`pa.roleName_${g.role}`])}
                  >
                    {g.status === 'pending_approval' ? t['pa.withdraw'] : t['pa.remove']}
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
        {/* 0016: on the school's written instruction, e.g. the primary left and there is no deputy. */}
        {sup && p.status === 'active' && !exp && !isPrimary && roles.some((r) => r.status === 'active') && (
          <div className="appoint-row">
            <button type="button" className="link-btn" onClick={() => open('appoint')}>
              {t['ap.action']}
            </button>
            <p className="form-hint">{t['ap.actionHint']}</p>
          </div>
        )}
      </section>

      <section className="panel p5" aria-labelledby={`${id}-as`}>
        <div className="panel-head">
          <h2 className="panel-title" id={`${id}-as`}>
            {t['pa.assignments']}
          </h2>
          {sup && isTeacher && p.status === 'active' && (
            <button type="button" className="btn btn-ghost" onClick={() => open('assign')}>
              {t['pa.assign']}
            </button>
          )}
        </div>
        {p.assignments.length === 0 ? (
          <p className="text-muted">{isTeacher ? t['pa.noClassesWho'] : t['pa.notATeacher']}</p>
        ) : (
          <ul className="school-list">
            {p.assignments.map((a) => (
              <li key={a.id}>
                <span>
                  {a.class_label ?? t['pa.gradeN'](a.grade_label ?? '')} · {t[`pa.kind_${a.kind}`]}
                </span>
                {sup && (
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => open({ unassign: a.id, label: a.class_label ?? a.grade_label ?? '' })}
                    aria-label={t['pa.unassignFrom'](a.class_label ?? a.grade_label ?? '')}
                  >
                    {t['pa.unassign']}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <AuditList scope="school" schoolId={s.id} targetId={p.id} title={t['pa.activity']} compact />

      {/* ── dialogs ── */}
      <Dialog
        open={act === 'edit'}
        title={t['pa.editDetails']}
        submitLabel={t['pa.save']}
        busy={busy}
        error={actError}
        onClose={close}
        onSubmit={() =>
          run(() => rpc('platform_update_person', { p_user_id: p.id, p_school_id: s.id, p_display_name: name, p_language: lang || null }))
        }
      >
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-en`}>
            {t['pa.name']}
          </label>
          <input id={`${id}-en`} className="field" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
        </div>
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-el`}>
            {t['pa.language']}
          </label>
          <span className="select-wrap">
            <select id={`${id}-el`} className="select" value={lang} onChange={(e) => setLang(e.target.value)}>
              <option value="">{t['pa.schoolDefault']}</option>
              <option value="fi">{t['pa.lang_fi']}</option>
              <option value="sv">{t['pa.lang_sv']}</option>
              <option value="en">{t['pa.lang_en']}</option>
            </select>
          </span>
        </div>
        {!p.is_test && <p className="form-hint">{t['pa.emailChangeLater']}</p>}
      </Dialog>

      <Dialog
        open={act === 'role'}
        wide
        title={t['pa.addRole']}
        submitLabel={result ? t['pa.close'] : t['pa.addRole']}
        busy={busy}
        error={actError}
        submitDisabled={!result && roleSpec.length === 0}
        onClose={close}
        hideSubmit={!!result}
        closeLabel={result ? t['pa.close'] : undefined}
        onSubmit={() =>
          run(
            () => rpc<{ status: string; route?: string }>('platform_grant_role', { p_user_id: p.id, p_school_id: s.id, p_role: roleSpec[0] }),
            (d) =>
              setResult(
                d.status === 'active' ? t['pa.roleAddedNow'] : d.route === 'platform' ? t['pa.roleToZencera'] : t['pa.roleToHead'],
              ),
          )
        }
      >
        {result ? (
          <p className="notice" role="status">
            {result}
          </p>
        ) : (
          <>
            <RolePicker value={roleSpec} onChange={setRoleSpec} classes={classes} exclude={held} single />
            {roleSpec[0] && (
              <div className="before-after">
                <p>
                  <strong>{t['pa.before']}</strong>{' '}
                  {roles.filter((r) => r.status === 'active').map((r) => roleReach(t, { role: r.role }, classes)).join(' · ') || t['pa.nothing']}
                </p>
                <p>
                  <strong>{t['pa.after']}</strong> {roleReach(t, roleSpec[0], classes)}
                </p>
                {roleSpec[0].role !== 'teacher' && !p.is_test && <p className="form-hint">{t['pa.wideningNote']}</p>}
              </div>
            )}
          </>
        )}
      </Dialog>

      <Dialog
        open={typeof act === 'object' && act !== null && 'revoke' in act}
        title={t['pa.removeRoleTitle'](typeof act === 'object' && act && 'revoke' in act ? act.label : '')}
        intro={<p className="form-hint">{t['pa.removeRoleIntro']}</p>}
        submitLabel={t['pa.remove']}
        danger
        busy={busy}
        error={actError}
        onClose={close}
        onSubmit={() => typeof act === 'object' && act && 'revoke' in act && run(() => rpc('platform_revoke_role', { p_grant_id: act.revoke }))}
      />

      <Dialog
        open={act === 'primary'}
        title={t['pa.makePrimaryAction']}
        intro={<p className="form-hint">{t['pa.makePrimaryIntro']}</p>}
        submitLabel={t['pa.makePrimaryAction']}
        busy={busy}
        error={actError}
        onClose={close}
        onSubmit={() => run(() => rpc('platform_set_primary_head', { p_school_id: s.id, p_user_id: p.id, p_confirmed_with: note }))}
      >
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-pc`}>
            {t['pa.confirmedWith']}
          </label>
          <input id={`${id}-pc`} className="field" value={note} onChange={(e) => setNote(e.target.value)} placeholder={s.paperwork_signatory ?? undefined} />
        </div>
      </Dialog>

      <Dialog
        open={act === 'appoint'}
        title={t['ap.title'](p.display_name)}
        intro={<p className="form-hint">{t['ap.intro']}</p>}
        submitLabel={result ? t['pa.close'] : t['ap.action'].replace(/…$/, '')}
        hideSubmit={!!result}
        closeLabel={result ? t['pa.close'] : undefined}
        submitDisabled={!instrFrom.trim() || !date}
        busy={busy}
        error={actError}
        onClose={close}
        onSubmit={() =>
          run(
            () =>
              rpc('platform_request_primary_head', {
                p_school_id: s.id,
                p_user_id: p.id,
                p_instruction_from: instrFrom.trim(),
                p_instruction_on: date,
                p_note: note.trim() || null,
              }),
            () => {
              setResult(t['ap.requested']);
              announce(t['ap.requested']);
            },
          )
        }
      >
        {result ? (
          <p className="notice" role="status">
            {result}
          </p>
        ) : (
          <>
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-apf`}>
                {t['ap.from']}
              </label>
              <input
                id={`${id}-apf`}
                className="field"
                maxLength={200}
                required
                aria-required="true"
                value={instrFrom}
                placeholder={s.paperwork_signatory ?? undefined}
                onChange={(e) => setInstrFrom(e.target.value)}
              />
            </div>
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-apd`}>
                {t['ap.on']}
              </label>
              <input
                id={`${id}-apd`}
                className="field"
                type="date"
                required
                aria-required="true"
                max={new Date().toISOString().slice(0, 10)}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-apn`}>
                {t['ap.note']}
              </label>
              <input id={`${id}-apn`} className="field" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          </>
        )}
      </Dialog>

      <Dialog
        open={act === 'assign'}
        wide
        title={t['pa.assignTitle'](p.display_name)}
        submitLabel={t['pa.assign']}
        busy={busy}
        error={actError}
        submitDisabled={picked.length === 0}
        onClose={close}
        onSubmit={() =>
          run(
            () =>
              rpc<{ added: number; second_homeroom: string[] }>('platform_assign', {
                p_user_id: p.id,
                p_kind: kind,
                p_class_ids: kind === 'grade_lead' ? null : picked,
                p_grade_id: kind === 'grade_lead' ? picked[0] : null,
              }),
            (d) => {
              setAct(null);
              announce(d.second_homeroom.length ? t['pa.secondHomeroom'](d.second_homeroom.join(', ')) : t['pa.saved']);
            },
          )
        }
      >
        <fieldset className="inline-radios">
          <legend className="field-label">{t['pa.kind']}</legend>
          {(['homeroom', 'subject', 'grade_lead'] as const).map((k) => (
            <label key={k}>
              <input
                type="radio"
                name={`${id}-k`}
                checked={kind === k}
                onChange={() => {
                  setKind(k);
                  setPicked([]);
                }}
              />{' '}
              {t[`pa.kind_${k}`]}
            </label>
          ))}
        </fieldset>
        <p className="form-hint">{t[`pa.kindHint_${kind}`]}</p>
        {kind === 'grade_lead' ? (
          <fieldset>
            <legend className="field-label">{t['pa.grade']}</legend>
            <span className="level-boxes">
              {(classes?.grades ?? []).map((g) => (
                <label key={g.id} className={`chip-check${picked.includes(g.id) ? ' on' : ''}`}>
                  <input type="radio" name={`${id}-g`} checked={picked.includes(g.id)} onChange={() => setPicked([g.id])} />
                  {t['pa.gradeN'](g.label)}
                </label>
              ))}
            </span>
          </fieldset>
        ) : (
          <fieldset>
            <legend className="field-label">{t['pa.classes']}</legend>
            <span className="level-boxes">
              {(classes?.classes ?? [])
                .filter((c) => c.status === 'active' && !p.assignments.some((a) => a.class_id === c.id && a.kind === kind))
                .map((c) => (
                  <label key={c.id} className={`chip-check${picked.includes(c.id) ? ' on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={picked.includes(c.id)}
                      onChange={(e) => setPicked(e.target.checked ? [...picked, c.id] : picked.filter((x) => x !== c.id))}
                    />
                    {c.label}
                  </label>
                ))}
            </span>
          </fieldset>
        )}
      </Dialog>

      <Dialog
        open={typeof act === 'object' && act !== null && 'unassign' in act}
        title={t['pa.unassignFrom'](typeof act === 'object' && act && 'unassign' in act ? act.label : '')}
        intro={<p className="form-hint">{t['pa.unassignIntro']}</p>}
        submitLabel={t['pa.unassign']}
        danger
        busy={busy}
        error={actError}
        onClose={close}
        onSubmit={() =>
          typeof act === 'object' &&
          act &&
          'unassign' in act &&
          run(
            () => rpc<{ session_open: boolean }>('platform_unassign', { p_assignment_id: act.unassign }),
            (d) => {
              setAct(null);
              announce(d.session_open ? t['pa.unassignedOpen'] : t['pa.saved']);
            },
          )
        }
      />

      <Dialog
        open={act === 'signout'}
        title={t['pa.signOutEverywhere']}
        intro={<p className="form-hint">{t['pa.signOutIntro'](p.display_name)}</p>}
        submitLabel={t['pa.signOutEverywhere']}
        busy={busy}
        error={actError}
        onClose={close}
        onSubmit={() => run(() => rpc('platform_sign_out_everywhere', { p_user_id: p.id, p_school_id: s.id }))}
      />

      <Dialog
        open={act === 'mfa'}
        title={t['pa.resetMfa']}
        intro={<p className="form-hint">{t['pa.resetMfaIntro']}</p>}
        submitLabel={t['pa.resetMfa']}
        danger
        busy={busy}
        error={actError}
        onClose={close}
        onSubmit={() => run(() => rpc('platform_reset_mfa', { p_user_id: p.id, p_school_id: s.id, p_confirmed_how: note }))}
      >
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-mh`}>
            {t['pa.confirmedHow']}
          </label>
          <input id={`${id}-mh`} className="field" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </Dialog>

      <Dialog
        open={act === 'password'}
        title={t['pa.resetPassword']}
        intro={!secret && <p className="form-hint">{t['pa.resetPasswordIntro']}</p>}
        submitLabel={t['pa.resetPassword']}
        hideSubmit={!!secret}
        closeLabel={secret ? t['pa.close'] : undefined}
        busy={busy}
        error={actError}
        onClose={close}
        onSubmit={() =>
          run(
            () => ops<{ username: string; password: string }>('reset_test_password', { user_id: p.id }),
            (d) => {
              setSecret(d.password);
              announce(t['pa.passwordReset']);
            },
          )
        }
      >
        {secret && (
          <div className="reveal" role="region" aria-label={t['pa.credsTitle']}>
            <p className="form-hint">{t['pa.credsOnce']}</p>
            <p>
              {t['pa.username']}: <code>{p.email}</code>
            </p>
            <p>
              {t['pa.password']}: <code className="secret">{secret}</code> <CopyButton text={secret} label={t['pa.copyPassword']} />
            </p>
          </div>
        )}
      </Dialog>

      <Dialog
        open={act === 'extend'}
        title={t['pa.extendExpiry']}
        intro={<p className="form-hint">{t['pa.extendIntro']}</p>}
        submitLabel={t['pa.extendExpiry']}
        busy={busy}
        error={actError}
        onClose={close}
        onSubmit={() => run(() => rpc('platform_extend_test_account', { p_user_id: p.id, p_days: days }))}
      >
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-xd`}>
            {t['pa.expiresIn']}
          </label>
          <span className="select-wrap">
            <select id={`${id}-xd`} className="select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {[90, 60, 30, 14, 7].map((d) => (
                <option key={d} value={d}>
                  {t['pa.nDays'](d)}
                </option>
              ))}
            </select>
          </span>
        </div>
      </Dialog>

      <Dialog
        open={act === 'deactivate'}
        title={t['pa.deactivateTitle'](p.display_name)}
        intro={
          <p className="form-hint">
            {p.other_schools > 0
              ? t['pa.deactivateOther']
              : t['pa.deactivateIntro'](p.display_name, p.assignments.length)}
          </p>
        }
        submitLabel={result ? t['pa.close'] : t['pa.deactivate']}
        hideSubmit={!!result}
        closeLabel={result ? t['pa.close'] : undefined}
        danger
        busy={busy}
        error={actError}
        onClose={close}
        onSubmit={() =>
          run(
            () =>
              rpc<{ assignments_ended: number; sole_teacher_of: string[] }>('platform_deactivate_person', {
                p_user_id: p.id,
                p_school_id: s.id,
                p_reason: reason,
                p_reactivate_on: reason === 'leave' && date ? date : null,
              }),
            (d) => {
              if (d.sole_teacher_of.length) setResult(t['pa.soleTeacher'](d.sole_teacher_of.join(', ')));
              else {
                setAct(null);
                announce(t['pa.deactivated']);
              }
            },
          )
        }
      >
        {result ? (
          <div className="notice" role="status">
            <p>{result}</p>
            <p>
              <Link to={`/admin/schools/${s.id}/classes`}>{t['pa.reassignNow']}</Link>
            </p>
          </div>
        ) : (
          <>
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-dr`}>
                {t['pa.reason']}
              </label>
              <span className="select-wrap">
                <select id={`${id}-dr`} className="select" value={reason} onChange={(e) => setReason(e.target.value)}>
                  {(['left_school', 'leave', 'role_ended', 'other'] as const).map((r) => (
                    <option key={r} value={r}>
                      {t[`pa.dr_${r}`]}
                    </option>
                  ))}
                </select>
              </span>
            </div>
            {reason === 'leave' && (
              <div className="field-group">
                <label className="field-label" htmlFor={`${id}-rd`}>
                  {t['pa.reactivateOn']}
                </label>
                <input id={`${id}-rd`} className="field" type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-describedby={`${id}-rdh`} />
                <p className="form-hint" id={`${id}-rdh`}>
                  {t['pa.optional']}
                </p>
              </div>
            )}
          </>
        )}
      </Dialog>

      <Dialog
        open={act === 'reactivate'}
        title={t['pa.reactivate']}
        submitLabel={t['pa.reactivate']}
        busy={busy}
        error={actError}
        onClose={close}
        onSubmit={() => run(() => rpc('platform_reactivate_person', { p_user_id: p.id, p_school_id: s.id, p_restore_assignments: restore }))}
      >
        <label className="check">
          <input type="checkbox" checked={restore} onChange={(e) => setRestore(e.target.checked)} /> {t['pa.restoreAssignments']}
        </label>
      </Dialog>

      <Dialog
        open={act === 'erase'}
        title={t['pa.eraseTitle'](p.display_name)}
        intro={
          <>
            <p className="form-hint">{t['pa.eraseIntro']}</p>
            {!p.is_test && (isAdminRole || roles.some((r) => r.role === 'head_of_school' && r.is_primary)) && (
              <p className="notice">{t['pa.eraseSecondAdmin']}</p>
            )}
          </>
        }
        submitLabel={result ? t['pa.close'] : t['pa.erase']}
        hideSubmit={!!result}
        closeLabel={result ? t['pa.close'] : undefined}
        danger
        submitDisabled={typed.trim().toLowerCase() !== (p.email ?? '').toLowerCase()}
        busy={busy}
        error={actError}
        onClose={() => {
          close();
          if (result === t['pa.erasedDone']) navigate(back);
        }}
        onSubmit={() =>
          run(
            () =>
              rpc<{ status: string }>('platform_erase_person', {
                p_user_id: p.id,
                p_school_id: s.id,
                p_reason: reason,
                p_note: note || null,
                p_confirm_email: typed,
              }),
            (d) => {
              if (d.status === 'erased') {
                announce(t['pa.erasedDone']);
                setAct(null);
                navigate(back);
              } else setResult(t['pa.eraseRequested']);
            },
          )
        }
      >
        {result ? (
          <p className="notice" role="status">
            {result}
          </p>
        ) : (
          <>
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-er`}>
                {t['pa.reason']}
              </label>
              <span className="select-wrap">
                <select id={`${id}-er`} className="select" value={reason} onChange={(e) => setReason(e.target.value)}>
                  {(['own_request', 'left_school', 'test_cleanup', 'other'] as const).map((r) => (
                    <option key={r} value={r}>
                      {t[`pa.er_${r}`]}
                    </option>
                  ))}
                </select>
              </span>
            </div>
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-en2`}>
                {isAdminRole && !p.is_test ? t['pa.instruction'] : t['pa.noteOptional']}
              </label>
              <input id={`${id}-en2`} className="field" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-et`}>
                {t['pa.typeEmail'](p.email ?? '')}
              </label>
              <input id={`${id}-et`} className="field" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} />
            </div>
          </>
        )}
      </Dialog>

      <EnterSupportDialog
        open={supportOpen}
        schoolId={s.id}
        schoolName={s.name}
        defaultReason={p.is_test ? 'test_accounts' : 'account_recovery'}
        onClose={() => setSupportOpen(false)}
        onEntered={async () => {
          setSupportOpen(false);
          await refreshSupport();
          await load();
        }}
      />
    </>
  );
}
